import { memo, useEffect } from "react";
import { useI18n } from "@/i18n";
import { typography, transitions, colors } from "@/design-system/tokens";

interface PlanLimitModalProps {
  message: string;
  onClose: () => void;
  onViewPlan: () => void;
}

export const PlanLimitModal = memo(function PlanLimitModal({
  message,
  onClose,
  onViewPlan,
}: PlanLimitModalProps) {
  const { t } = useI18n();

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 300,
        padding: "24px",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "20px",
          padding: "36px 32px",
          width: "100%",
          maxWidth: "440px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "20px",
          textAlign: "center",
        }}
      >
        {/* Warning icon */}
        <div
          style={{
            width: "52px",
            height: "52px",
            background: "#fef3c7",
            borderRadius: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#d97706"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        {/* Title */}
        <h2
          style={{
            fontFamily: typography.fontFamily,
            fontSize: typography.size.lg,
            fontWeight: typography.weight.bold,
            color: "#111",
            margin: 0,
          }}
        >
          {t.planLimit.title}
        </h2>

        {/* Backend message */}
        <p
          style={{
            fontFamily: typography.fontFamily,
            fontSize: typography.size.sm,
            color: "#555",
            margin: 0,
            lineHeight: "1.6",
          }}
        >
          {message}
        </p>

        {/* Support text */}
        <p
          style={{
            fontFamily: typography.fontFamily,
            fontSize: typography.size.xs,
            color: "#999",
            margin: 0,
          }}
        >
          {t.planLimit.viewPlan}
        </p>

        {/* Actions */}
        <div style={{ display: "flex", gap: "10px", marginTop: "8px", width: "100%" }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: "100%",
              fontFamily: typography.fontFamily,
              fontSize: typography.size.sm,
              fontWeight: typography.weight.medium,
              color: "#666",
              background: "transparent",
              border: "1px solid #e0e0e0",
              borderRadius: "10px",
              padding: "12px 20px",
              cursor: "pointer",
              transition: `background ${transitions.fast}`,
            }}
          >
            {t.planLimit.close}
          </button>
        </div>
      </div>
    </div>
  );
});
